import classNames from 'classnames';
import { useAtom } from 'jotai';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useResetRecoilState } from 'recoil';
import DropdownSelector from '../../components/actions/DropdownSelector';
import SortOptionsSelector from '../../components/actions/SortOptionsSelector';
import * as Icon from '../../components/icons';
import { ActionBar } from '../../components/nav/ActionBar';
import useActualityAggregationMetricDescriptors from '../hooks/useActualityAggregationMetricDescriptors';
import {
  actualityAggregatedStatsGroupBy,
  actualityAggregatedStatsGroupBySecondary,
  actualityAggregatedStatsGroupsSortByDirection,
  actualityAggregatedStatsGroupsSortMode,
  actualityAggregatedStatsSelectedAggregatedMetric,
  actualityAggregatedStatsSelectedGroups,
} from '../store/actuality.kpi.state';
import {
  actualityAggregatedStatsColorBySelected,
  actualityAggregatedStatsTooltipModeSelected,
} from '../store/actuality.kpi.ui.state';
import ActualityAggregateByGroupSelector from './ActualityAggregateByGroupSelector';
import ActualityAggregateMetricSelector from './ActualityAggregateMetricSelector';

const AggregationControls: React.FC = () => {
  const { t } = useTranslation('feed');
  const [aggregatedMetric, setAggregatedMetric] = useRecoilState(
    actualityAggregatedStatsSelectedAggregatedMetric,
  );
  const [aggregateBy, setAggregateBy] = useRecoilState(
    actualityAggregatedStatsGroupBy,
  );
  const [subGroup, setSubGroup] = useRecoilState(
    actualityAggregatedStatsGroupBySecondary,
  );

  const [groupSortMode, setGroupSortMode] = useRecoilState(
    actualityAggregatedStatsGroupsSortMode,
  );

  const [groupSortDirection, setGroupSortDirection] = useRecoilState(
    actualityAggregatedStatsGroupsSortByDirection,
  );
  const [colorBySelected, setColorBySelected] = useAtom(
    actualityAggregatedStatsColorBySelected,
  );
  const [tooltipModeSelected, setTooltipModeSelected] = useAtom(
    actualityAggregatedStatsTooltipModeSelected,
  );

  // Use separate panel state for AggregationControls
  const panelId = 'AggregationControls';
  const resetSelectedGroups = useResetRecoilState(
    actualityAggregatedStatsSelectedGroups,
  );
  const metricDescriptors = useActualityAggregationMetricDescriptors();

  // Create separate dropdown options configs
  const settingsOptions = _.keyBy(
    [
      {
        key: 'colorByGroup',
        title: t`Color by Group`,
        icon: <Icon.Settings4 className="h-4 w-4 fill-current" />,
        status: colorBySelected === 'group' ? 'active' : 'inactive',
        onClick: () => setColorBySelected('group'),
      },
      {
        key: 'colorByBucket',
        title: t`Color by Metric`,
        icon: <Icon.Settings4 className="h-4 w-4 fill-current" />,
        status: colorBySelected === 'bucket' ? 'active' : 'inactive',
        onClick: () => setColorBySelected('bucket'),
      },
      {
        key: 'divider1',
        title: '---',
        icon: null,
        status: '',
        onClick: () => {},
      },
      {
        key: 'tooltipAll',
        title: t`Show All Tooltips`,
        icon: <Icon.CircleInfo className="h-4 w-4 fill-current" />,
        status: tooltipModeSelected === 'all' ? 'active' : 'inactive',
        onClick: () => setTooltipModeSelected('all'),
      },
      {
        key: 'tooltipCurrent',
        title: t`Show Current Tooltip`,
        icon: <Icon.CircleInfo className="h-4 w-4 fill-current" />,
        status: tooltipModeSelected === 'current' ? 'active' : 'inactive',
        onClick: () => setTooltipModeSelected('current'),
      },
    ],
    'key',
  );

  return (
    <ActionBar
      data-component="AggregationControls"
      transparent
      className={classNames(
        'p-2',
        // 'bg-app-panel-light/40',
        'bg-sidebar-title/75',
        'saturate-120 backdrop-blur',
        'top-header_title sticky',
        'flex-col items-center',
        'gap-2',
        '!z-header_screen',
      )}
    >
      <div className="flex w-full items-center gap-2">
        <ActualityAggregateByGroupSelector
          value={aggregateBy}
          onChange={v => {
            setAggregateBy(v);
            resetSelectedGroups();
          }}
        />
        <div className="text-menu-text text-minimal">{t`by`}</div>
        <ActualityAggregateByGroupSelector
          value={subGroup}
          onChange={v => setSubGroup(v)}
        />
      </div>
      <div className="flex w-full items-center gap-2">
        <ActualityAggregateMetricSelector
          value={aggregatedMetric}
          onChange={id => setAggregatedMetric(id)}
        />

        {/* Unified Sort Options with 4 options */}
        <SortOptionsSelector
          panelId={panelId}
          sortMode={groupSortMode}
          sortDirection={groupSortDirection}
          onChange={value => {
            setGroupSortMode(value.mode);
            setGroupSortDirection(value.direction);
          }}
          // UI configuration for unified sort
          showUnifiedSort={true}
          showSortDirection={false}
          showSortMetric={false}
          showGroupSort={false}
          showSortModeToggle={false}
          compact={false}
          panelMode={true}
          vertical={true}
          w_xl={true}
          DropAlignRight={true}
          sortDirectionLabel={t('Sort')}
        />

        {/* Settings Dropdown */}
        <DropdownSelector
          className="shrink-0 text-xs"
          value="settingsOptions"
          values={_.keys(settingsOptions)}
          renderValue={(key: string) => {
            const option = settingsOptions[key as string];
            if (!option) return null;
            if (key.startsWith('divider')) {
              return <div className="border-menu-active/20 my-1 border-t" />;
            }
            return (
              <div
                className={`flex items-center ${option.status === 'active' ? 'text-menu-active' : ''}`}
              >
                <div className="flex items-center">
                  <div className="flex h-4 w-4 items-center justify-center">
                    {option.status === 'active' && (
                      <Icon.CircleOk className="h-4 w-4 fill-current" />
                    )}
                  </div>
                  <span className="mx-2">{option.title}</span>
                </div>
              </div>
            );
          }}
          renderValueSelected={() => (
            <div className="flex items-center">
              <Icon.Settings className="h-4 w-4 fill-current" />
              <span className="ml-1">{t`Settings`}</span>
            </div>
          )}
          onChange={(key: string) => {
            const option = settingsOptions[key as string];
            if (option && _.isFunction(option.onClick)) {
              option.onClick();
            }
          }}
          vertical
          panelMode
          w_xl
          DropAlignRight
          iconValue={Icon.Settings}
          iconValueOnly
          iconClass={classNames('w-4 h-4 xl:w-5 xl:h-5')}
        />
      </div>
    </ActionBar>
  );
};

export default AggregationControls;
